/**
 * @since 2025-02-24
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import DialogConfirmJobAction from "components/Shared/DialogConfirmJobAction";
import { useEffect, useRef, useState } from "react";
import { IoMdFastforward } from "react-icons/io";
import "./JobActions.scss";

const JobActions = ({ job, onStatusChangeRequest }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [actionName, setActionName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editedCommandLine, setEditedCommandLine] = useState("");

  const menuRef = useRef(null);

  const handleMenuSelect = (option) => {
    if (option === "EDIT_AND_RERUN") {
      setActionName("READY");
      setEditedCommandLine(job.CommandLine);
      setShowEditModal(true);
      setMenuOpen(false);
      return;
    }
    setActionName(option);
    setShowConfirm(true);
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const confirmAction = () => {
    setShowConfirm(false);
    onStatusChangeRequest(job, actionName, editedCommandLine);
  };

  const cancelConfirm = () => {
    setShowConfirm(false);
  };

  const confirmEditAndRerun = () => {
    // Send a command line only when it was edited: "" means "keep the job's command line"
    const newCommandLine =
      editedCommandLine !== job.CommandLine ? editedCommandLine : "";
    onStatusChangeRequest(job, actionName, newCommandLine);
    setEditedCommandLine("");
    setShowEditModal(false);
  };

  const cancelEditAndRerun = () => {
    setEditedCommandLine("");
    setShowEditModal(false);
  };

  return (
    <div className="job-actions" ref={menuRef}>
      <button
        className="job-actions__button"
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-label="Actions"
      >
        <IoMdFastforward size={22} />
      </button>

      {menuOpen && (
        <div className="job-actions__menu">
          <button onClick={() => handleMenuSelect("READY")}>Re-run</button>
          <button onClick={() => handleMenuSelect("EDIT_AND_RERUN")}>
            Edit &amp; Re-run
          </button>
          <button onClick={() => handleMenuSelect("OK")}>Set as OK</button>
          <button onClick={() => handleMenuSelect("WARNING")}>
            Set as Warning
          </button>
          <button onClick={() => handleMenuSelect("ERROR")}>
            Set as Error
          </button>
          <button
            onClick={() => handleMenuSelect("FORCE_START_CHILDREN_WAITING")}
          >
            Force Start
          </button>
        </div>
      )}

      {showConfirm && (
        <div className="job-actions__modal-overlay" onClick={cancelConfirm}>
          <DialogConfirmJobAction
            listJobs={[job]}
            actionName={actionName}
            onActionConfirmed={confirmAction}
            onActionCanceled={cancelConfirm}
          />
        </div>
      )}

      {showEditModal && (
        <div
          className="job-actions__modal-overlay"
          onClick={cancelEditAndRerun}
        >
          <div
            className="job-actions__edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Edit Job & Re-run</h3>
            <textarea
              value={editedCommandLine}
              onChange={(e) => setEditedCommandLine(e.target.value)}
              rows={4}
              className="job-actions__edit-modal-textarea"
            />
            <div className="job-actions__edit-modal-actions">
              <button
                onClick={confirmEditAndRerun}
                className="job-actions__edit-modal-button"
              >
                Re-run
              </button>
              <button
                onClick={cancelEditAndRerun}
                className="job-actions__edit-modal-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobActions;
